import { z } from 'zod';
import { Board, BoardFingerprint, BoardJob, BoardSyncApp, DesktopPlatform, RemoveReason, UpdateFailureReason, } from './boards.js';
import { Ulid } from './common.js';
import { isValidUsername } from './ids.js';
/**
 * Reaching a Board (`docs/off-board-plan.md` §2.3, §2.4).
 *
 * Three things live here, and they are one file because each is a face of the same seam — the one
 * place where a browser tab and the desktop app's window really differ:
 *
 * 1. **`BoardLink`** — the interface every storefront screen talks to. Types only; the three
 *    implementations are the storefront's (`apps/web/src/storefront/link/`).
 * 2. **The desktop bridge contract, version 1** — the `bf_*` IPC commands the desktop app's window
 *    may call, the events the native side emits, and a schema for every payload. The Rust side
 *    mirrors this file mechanically: one command per entry of {@link BRIDGE_COMMANDS}, one serde
 *    struct per schema, every key camelCase.
 * 3. **Deep links** — the four `boardflight://` forms, parsed and formatted in one place.
 *
 * What the seam must never let through, whatever is added to it:
 *
 * - **A Board's token.** No command returns one and no event carries one. It lives in the OS
 *   keychain and is attached, by native code, only to requests to that Board.
 * - **A refresh token.** The page asks for an access token and gets exactly that.
 * - **A page-side "yes" to something destructive.** Removing a game, unpairing, and installing
 *   with the game removed first raise a dialog the native side draws. A script on the page can ask;
 *   it cannot answer.
 * - **A request to a Board from the page.** A Board refuses any web origin (plan §1), the desktop
 *   window's included, so every Board request is made by native code, which sends no `Origin`.
 */
/* ---------------------------------------------------------------- what a Board says */
/**
 * `GET /board/info`, as a tolerant client hands it on.
 *
 * Every field survives being missing: a body that is not the expected shape still yields this,
 * with an empty `capabilities` and three nulls. `capabilities` is the feature-detection surface —
 * prefer it to reading `os` (`docs/board-api.md`).
 */
export const BoardInfo = z.object({
    capabilities: z.array(z.string()),
    model: z.string().nullable(),
    name: z.string().nullable(),
    /** The `os` field: `2.5.3`. */
    os: z.string().nullable(),
});
/**
 * One entry of `GET /v1/apps`, as a tolerant client hands it on (`BoardInstalledApp` in Kotlin).
 *
 * `packages/board-fixtures` holds the raw bodies this is parsed from, by firmware, and the exact
 * value each must produce.
 */
export const BoardInstalledApp = z.object({
    /** The value the launch, stop and remove routes take: a package name for an APK, an opaque id for a web app. */
    id: z.string(),
    /** An APK's identity. For a web app, the browser host every web app shares — never an identity. */
    packageName: z.string().nullable(),
    /** A web app's identity, and the only field that says which web app it is. */
    packageId: z.string().nullable(),
    name: z.string(),
    /** Observed: `apk`, `webapp`. Null is read as an APK. */
    kind: z.string().nullable(),
    versionName: z.string().nullable(),
    /** An APK only. No web app entry has carried one on any firmware seen. */
    versionCode: z.number().int().nullable(),
    /** Null on every firmware since OS 2.4.1, which does not say. Null is "no idea", never "no". */
    running: z.boolean().nullable(),
});
/* ------------------------------------------------------------------------ BoardLink */
/** How a Board was found. `typed` is an address somebody entered. */
export const DiscoveryTransport = z.enum(['mdns', 'sweep', 'remembered', 'typed']);
/**
 * A Board somebody could pair with: something on this network that answered `/board/info`.
 *
 * `address` is what pairing is addressed to, because a Board that has never been paired has no
 * other handle. Everything after pairing goes by the account's `boardId`.
 */
export const FoundBoard = z.object({
    /** `host:port`, the port always filled in: `192.168.0.189:8843`. */
    address: z.string(),
    /** Null for a Board that reported neither a model nor a name. */
    fingerprint: BoardFingerprint.nullable(),
    name: z.string().nullable(),
    model: z.string().nullable(),
    os: z.string().nullable(),
    transport: DiscoveryTransport,
    /** This computer already holds a token the Board still accepts. */
    paired: z.boolean(),
});
/**
 * What this computer can say about one of the account's Boards, right now.
 *
 * Only a link that can reach a Board fills it in. From a phone it is null, and the Board's `reach`
 * — which desktop saw it, and when — is all there is to say.
 */
export const BoardHere = z.object({
    /** This computer holds a token for it. */
    paired: z.boolean(),
    /** It answered `/board/info` the last time it was asked. */
    reachable: z.boolean(),
    /** Where it was last seen, `host:port`. */
    address: z.string().nullable(),
    /** When this computer last got an answer from it. */
    seenAt: z.number().int().nullable(),
});
/** One of the account's Boards, as a link returns it: the account's row, and this computer's view. */
export const LinkedBoard = Board.extend({ here: BoardHere.nullable() });
/* ---------------------------------------------------------- the desktop bridge, v1 */
/**
 * The bridge contract's version — `bridge` in {@link BridgeInfo} and on the account's `desktops`
 * row.
 *
 * Bumped only for a change an older page or an older app would get wrong. Adding a command or an
 * event is not one: that is what `capabilities` is for, and a page finds out by asking.
 */
export const BRIDGE_VERSION = 1;
/** The scheme the IPC origin grant is written against: these commands, and no others. */
export const BRIDGE_COMMAND_PREFIX = 'bf_';
/**
 * What a bridge can do, by name — `capabilities` in {@link BridgeInfo}.
 *
 * A page treats a missing one the way the client treated a missing Board capability: it hides the
 * control and, where the gap matters, says "Update BoardFlight". The wire is a list of strings, so
 * an app newer than this file can name one this file has not heard of.
 */
export const BRIDGE_CAPABILITIES = [
    /** Device-code sign-in held by the native side. */
    'session',
    /** Finding Boards at all. */
    'discover',
    /** mDNS is among the transports. Absent where it is switched off (plan §12 H7). */
    'discover_mdns',
    'pair',
    /** The six-digit fallback. */
    'pair_code',
    'install',
    /** Installing with the game removed first. */
    'install_remove_first',
    /** *Switch pieces*: `bf_install` takes `switching`. */
    'switch_pieces',
    'launch',
    'stop',
    'remove',
    /** Run in the background, start at login. */
    'settings',
    /** The app can update itself. */
    'self_update',
    'open_external',
    /**
     * The app draws Library, Search, My Games and My Account as tabs of its own, along the bottom of a
     * phone's screen, and the page leaves its own switch, account controls and search field out
     * (`docs/phone-app-plan.md` §9).
     */
    'native_tabs',
    /**
     * The system's own notifications, sent from BoardFlight's servers: `bf_push_state` and
     * `bf_push_enable` (`docs/phone-app-plan.md` §10). Named only by a build that can receive one, a
     * phone's — APNs on iOS, and FCM on Android once that app exists. A desktop shows its
     * notifications itself and never names it.
     */
    'push',
];
/**
 * How a command fails: the value a rejected `invoke` carries.
 *
 * `code` is stable and is what a page branches on; `message` is for a person. `boardMessage` is
 * Board's own text, verbatim, whenever Board is what refused — never paraphrased, because it is the
 * only place an artifact's real problem is ever stated.
 */
export const BRIDGE_ERROR_CODES = [
    /** This app has no such command, or the capability is off on this system. */
    'unsupported',
    /** The arguments did not match the command's schema. */
    'invalid_args',
    /** There is no session. */
    'signed_out',
    /** BoardFlight's API could not be reached, or refused. */
    'api_unreachable',
    /** No Board by that id or address is known to this computer. */
    'board_unknown',
    /** This computer holds no token for that Board. */
    'not_paired',
    /** The Board did not answer at all. */
    'board_unreachable',
    /** Something answered, and it was not a Board. */
    'not_a_board',
    /** The Board no longer accepts this computer's token (`401`). Pair again. */
    'board_unauthorized',
    /** The Board answered a pairing request with an error. */
    'pair_refused',
    /** Nobody answered the Board's prompt in time. The prompt may still be on screen. */
    'pair_timeout',
    /** The Board's pairing window is not open (`409 not_pairing`). */
    'not_pairing',
    /** The Board refused something else. `boardMessage` and `httpStatus` say what. */
    'board_refused',
    /** That game is not on that Board. */
    'not_installed',
    /** The person answered the native side's confirmation with no. */
    'declined',
    /** There is nothing left to withdraw: the upload to the Board has begun, or the job has ended. */
    'too_late',
    /** Anything else. */
    'error',
];
export const BridgeError = z.object({
    /** One of {@link BRIDGE_ERROR_CODES}; a string, so a newer app's code still parses. */
    code: z.string(),
    message: z.string(),
    boardMessage: z.string().nullable().optional(),
    /** The Board's HTTP status, when the Board answered. */
    httpStatus: z.number().int().nullable().optional(),
});
/**
 * What a failed pairing is reported as — `props.reason` of `onboarding_pair_failed`.
 *
 * Sorted by what was observed and never by a guess: the Board answered with an error, the Board
 * did not answer in time, the Board could not be reached at all, or something else.
 */
export function pairFailureReason(code) {
    if (code === 'pair_refused' || code === 'not_pairing' || code === 'board_refused')
        return 'refused';
    if (code === 'pair_timeout')
        return 'timeout';
    if (code === 'board_unreachable')
        return 'unreachable';
    return 'error';
}
/** `bf_bridge_info` — who is on the other side. */
export const BridgeInfo = z.object({
    /** The desktop app's version: `1.4.0`. */
    version: z.string(),
    /** {@link BRIDGE_VERSION} as this app speaks it. */
    bridge: z.number().int().min(1),
    platform: DesktopPlatform,
    capabilities: z.array(z.string()),
});
/** A device-code sign-in in flight: the code to show, and where it is approved. */
export const BridgeSignIn = z.object({
    /** Grouped for reading: `WXYZ-4821`. */
    userCode: z.string(),
    verificationUri: z.string(),
    verificationUriComplete: z.string(),
    /** Epoch ms after which the code is dead and a new one is needed. */
    expiresAt: z.number().int(),
});
/**
 * The session the native side holds, as much of it as the page may know.
 *
 * `signIn` is set only while `signing_in`. There is no token in here; a page that needs one asks
 * `bf_session_token`.
 */
export const BridgeSession = z.object({
    status: z.enum(['signed_out', 'signing_in', 'signed_in']),
    userId: Ulid.nullable(),
    signIn: BridgeSignIn.nullable(),
});
/** An access token, and nothing that could mint another. */
export const BridgeAccessToken = z.object({
    accessToken: z.string(),
    /** Epoch ms. */
    accessTokenExpiresAt: z.number().int(),
});
/** A Board this computer has paired with, or remembers. */
export const BridgeBoard = z.object({
    /** The account's id for it. Null until the first sync has reached BoardFlight. */
    boardId: Ulid.nullable(),
    fingerprint: BoardFingerprint,
    name: z.string().nullable(),
    model: z.string().nullable(),
    os: z.string().nullable(),
    here: BoardHere,
});
/** What a read of a Board's apps came to. Entitled games only — see `bf_board_apps`. */
export const BridgeBoardApps = z.object({
    boardId: Ulid,
    /** When the Board answered. */
    readAt: z.number().int(),
    items: z.array(BoardSyncApp),
});
export const BridgeSettings = z.object({
    /** Keep running with the window closed: a tray or menu-bar icon (decision 10). */
    background: z.boolean(),
    /** Start when the person signs in to their computer. */
    autostart: z.boolean(),
});
export const BridgeUpdate = z.object({
    /** The version running now. */
    current: z.string(),
    /** A newer released version, or null when this is the newest. */
    available: z.object({ version: z.string(), notes: z.string().nullable() }).nullable(),
});
/**
 * Whether the system lets this app show a notification: the person has not been asked yet
 * (`not_determined`), said yes, said no, or — iOS's quiet kind — lets them arrive in the
 * notification centre without a sound or a banner (`provisional`).
 */
export const PushPermission = z.enum(['granted', 'denied', 'not_determined', 'provisional']);
/**
 * `bf_push_state` / `bf_push_enable`. **Never the device's token**: that goes from the native side
 * to BoardFlight and to nobody else (`PUT /v1/me/push-devices`). `registered` says the token is on
 * the signed-in account, which is false with nobody signed in.
 */
export const BridgePushState = z.object({
    permission: PushPermission,
    registered: z.boolean(),
});
/** A `host[:port]` somebody typed. The native side decides whether it may be asked — see `bf_probe_board`. */
const BoardAddress = z.string().trim().min(1).max(255);
const BoardAndApp = z.object({ boardId: Ulid, appId: Ulid });
const NoArgs = z.object({});
/**
 * Every command the desktop app's window may call, with the shape of what it takes and returns.
 *
 * **Calling convention.** `invoke(name, args)`: `args` is one object whose keys are the ones in
 * the command's `args` schema, camelCase. A command resolves with its `result` or rejects with a
 * {@link BridgeError}. A command that returns nothing resolves with `null`.
 *
 * **The grant.** IPC is granted to `https://boardflight.app/*` and only for names beginning
 * {@link BRIDGE_COMMAND_PREFIX}. Nothing here gives the page a file, a shell or an HTTP client.
 */
export const BRIDGE_COMMANDS = {
    /**
     * Who is on the other side: the app's version, the bridge version it speaks, the system it runs
     * on and what it can do. The first call a page makes, and the only one it may assume exists.
     */
    bf_bridge_info: { args: NoArgs, result: BridgeInfo },
    /** The session as it stands. Never a token. */
    bf_session_state: { args: NoArgs, result: BridgeSession },
    /**
     * Start a device-code sign-in (`POST /v1/auth/device/start`, kind `device`) and open the system
     * browser at the approval page. The native side polls; the outcome arrives as `bf:session`.
     * Calling it again replaces a code that is still pending.
     */
    bf_session_start: {
        args: z.object({
            /** False to only show the code — a second attempt, when the browser is already there. */
            openBrowser: z.boolean().optional(),
        }),
        result: BridgeSignIn,
    },
    /**
     * An access token for `api.boardflight.app`, refreshed first when it has less than
     * `minValidityMs` left, or null when signed out. The refresh token never crosses: it rotates on
     * every use, so two holders would sign each other out.
     */
    bf_session_token: {
        args: z.object({ minValidityMs: z.number().int().min(0).max(600_000).optional() }),
        result: BridgeAccessToken.nullable(),
    },
    /** Revoke the session and forget it. Board tokens stay: they belong to the computer, not the account. */
    bf_session_sign_out: { args: NoArgs, result: z.null() },
    /** The Boards this computer has paired with or remembers, each with its view from here. */
    bf_boards: { args: NoArgs, result: z.object({ items: z.array(BridgeBoard) }) },
    /**
     * Look for Boards: one bounded pass over mDNS, the subnet sweep and remembered addresses. Each
     * Board arrives as a `bf:board_found` event carrying `discoveryId`, and the pass ends with one
     * `bf:discovery_finished`. Resolves at once with the id; it does not wait for the pass.
     */
    bf_discover: { args: NoArgs, result: z.object({ discoveryId: z.string() }) },
    /**
     * Ask one typed address whether it is a Board (`GET /board/info`, no token).
     *
     * The native side refuses anything that is not a private-range or link-local address or a
     * `.local` name, so a script on the page cannot use this to make the computer call an arbitrary
     * host.
     */
    bf_probe_board: { args: z.object({ address: BoardAddress }), result: FoundBoard },
    /**
     * Tap-to-approve pairing (`POST /v1/pair/request`). The Board shows its own prompt and this does
     * not resolve until somebody answers it or the wait gives up — a minute or more. Leaving the page
     * does not withdraw the prompt. On success the token goes to the keychain, the Board is synced to
     * the account, and the result carries its `boardId`.
     */
    bf_pair: { args: z.object({ address: BoardAddress }), result: BridgeBoard },
    /** The six-digit fallback (`POST /v1/pair`), for a Board whose pairing window is open. */
    bf_pair_code: {
        args: z.object({ address: BoardAddress, code: z.string().regex(/^[0-9]{6}$/) }),
        result: BridgeBoard,
    },
    /**
     * Forget a Board on this computer: drop its token. **Confirmed natively.** The account's row is
     * the page's to delete (`DELETE /v1/me/boards/:boardId`), afterwards.
     */
    bf_unpair: { args: z.object({ boardId: Ulid }), result: z.null() },
    /**
     * Read a Board's apps now (`GET /v1/apps`), sync what was read to the account, and answer with
     * the same list. **Entitled games only**: the native side matches Board's list against the
     * account's library before anything leaves it, and that includes leaving it for the page.
     */
    bf_board_apps: { args: z.object({ boardId: Ulid }), result: BridgeBoardApps },
    /**
     * Install or update: create the job (`source: 'desktop'`), claim it and run the pipeline.
     * Resolves with the job as soon as it exists; progress arrives as `bf:job` events.
     *
     * With `removeFirst` the game is removed before the upload, **which destroys its saved data, and
     * the native side confirms it first** — `declined` if the person says no.
     *
     * With `switching` (capability `switch_pieces`) the build named is another sibling of the game
     * that is installed — *Switch pieces*. It is installed over the copy that is there like any
     * other build; what `switching` changes is how a refusal is read: one the Board words ends the
     * job `failed` with `removeFirst: 'switch_refused'`, the offer to remove the game and try again,
     * instead of a dead end. It is kept on the native side and is no part of the job BoardFlight
     * holds, so nothing queued anywhere else can carry it.
     */
    bf_install: {
        args: z
            .object({
            boardId: Ulid,
            appId: Ulid,
            buildId: Ulid.optional(),
            removeFirst: z.boolean().optional(),
            switching: z.boolean().optional(),
        })
            // A switch names the build it switches to: with none, a job means the installed sibling's own.
            .refine((args) => args.switching !== true || args.buildId !== undefined, {
            message: 'A switch must name the build to switch to.',
            path: ['buildId'],
        }),
        result: BoardJob,
    },
    /**
     * Stop a job: one this desktop is running, while it can still be stopped — before the upload
     * begins — or one still waiting. `too_late` once the build is going to the Board, and for a job
     * that has already ended; the message says which.
     */
    bf_cancel: { args: z.object({ jobId: Ulid }), result: z.null() },
    /** Ask the Board to run a game (`POST /v1/apps/{id}/launch`). */
    bf_launch: { args: BoardAndApp, result: z.null() },
    /**
     * Ask the Board to stop a game (`POST /v1/apps/{id}/stop`). A web app cannot be stopped
     * (`422 stop_unsupported`), and no firmware since OS 2.4.1 says whether anything is running, so
     * the storefront asks it only of a game its own window started — before starting another, and
     * from the band that says what was started.
     */
    bf_stop: { args: BoardAndApp, result: z.null() },
    /** Remove a game and its saved data (`DELETE /v1/apps/{id}`). **Confirmed natively.** */
    bf_remove: { args: BoardAndApp, result: z.null() },
    /** Whether the app keeps running with its window closed, and whether it starts at login. */
    bf_settings_get: { args: NoArgs, result: BridgeSettings },
    /** Change either answer; a key left out is left alone. Answers with both as they now stand. */
    bf_settings_set: {
        args: z.object({ background: z.boolean().optional(), autostart: z.boolean().optional() }),
        result: BridgeSettings,
    },
    /** Ask whether a newer released version of the app exists. */
    bf_update_check: { args: NoArgs, result: BridgeUpdate },
    /** Download the newer version, install it and restart. Progress arrives as `bf:update`. */
    bf_update_install: { args: NoArgs, result: z.null() },
    /**
     * Open a link in the system browser — anything that leaves the storefront. `https:` only, and the
     * native side checks again.
     */
    bf_open_external: {
        args: z.object({ url: z.url().refine((url) => url.startsWith('https://'), 'Must be an https URL.') }),
        result: z.null(),
    },
    /**
     * Whether the system lets this app notify, and whether this device is registered for the
     * signed-in account's notifications (capability `push`). Asks nobody anything.
     */
    bf_push_state: { args: NoArgs, result: BridgePushState },
    /**
     * Do what it takes to turn notifications on, and answer with how they then stand. Asked the first
     * time, it raises the system's own question; once the answer is yes, the device registers with
     * the system and the native side gives its token to BoardFlight for the signed-in account. Once
     * the answer is no, only the system's Settings can change it, so this opens the app's page there
     * and answers `denied`. Nothing the page passes can answer the system's question.
     */
    bf_push_enable: { args: NoArgs, result: BridgePushState },
};
export const BRIDGE_COMMAND_NAMES = Object.keys(BRIDGE_COMMANDS);
/**
 * Where an install is, more finely than a job's status says (`InstallCoordinator.Step` in Kotlin).
 *
 * `verifying` is the SHA-256 check between download and upload; `removing` is the first half of an
 * install with the game removed first; `finishing` is the wait between Board accepting the build
 * and Board listing it.
 */
export const PipelineStep = z.enum([
    'preparing',
    'downloading',
    'verifying',
    'removing',
    'installing',
    'finishing',
    'done',
    'failed',
    'cancelled',
]);
/**
 * Every event the native side emits. A page listens; it never emits one.
 *
 * Progress carries data, because bytes cannot be re-read. Everything else is a nudge in the
 * broker's sense — it says what changed, and the page re-reads it.
 */
export const BRIDGE_EVENTS = {
    /** The session changed: a sign-in was approved, declined or expired, or somebody signed out. */
    'bf:session': BridgeSession,
    /** One Board found by the discovery pass `discoveryId`. */
    'bf:board_found': z.object({ discoveryId: z.string(), board: FoundBoard }),
    /** A discovery pass ended. `tried` is what it looked with, whether or not it found anything. */
    'bf:discovery_finished': z.object({
        discoveryId: z.string(),
        tried: z.array(DiscoveryTransport),
        found: z.number().int().min(0),
    }),
    /** This computer's Boards changed — paired, forgotten, reachable or not. Re-read `bf_boards`. */
    'bf:boards': z.null(),
    /** What is on a Board changed, as this computer read it. Re-read the library for that Board. */
    'bf:board_apps': z.object({ boardId: Ulid }),
    /** A job this desktop is running moved: a new status, a new step, or more bytes. */
    'bf:job': z.object({
        job: BoardJob,
        step: PipelineStep,
        bytesDone: z.number().int().min(0).nullable(),
        bytesTotal: z.number().int().min(0).nullable(),
        /** Set when the step is `failed` and removing the game first would let it through. */
        removeFirst: RemoveReason.nullable(),
    }),
    /** The app's own update is under way. */
    'bf:update': z.object({
        phase: z.enum(['downloading', 'installing', 'restarting', 'failed']),
        progress: z.number().min(0).max(1).nullable(),
        /**
         * Why, when the phase is `failed`; null otherwise. Absent from an app older than 0.1.22, which
         * could only say that it had not worked.
         */
        reason: UpdateFailureReason.nullish(),
    }),
    /** A deep link arrived that the page should show. `job` links are run natively and still arrive. */
    'bf:deep_link': z.object({ url: z.string() }),
};
export const BRIDGE_EVENT_NAMES = Object.keys(BRIDGE_EVENTS);
/* ---------------------------------------------------------------------- deep links */
/** `boardflight://` — registered by the desktop app, fired by the storefront in a browser. */
export const DEEP_LINK_SCHEME = 'boardflight';
const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/;
/** A share code as stored (`ShareCode` in `store.ts`): six of lower-case Crockford base32. */
const SHARE_CODE_RE = /^[0-9a-hjkmnp-tv-z]{6}$/;
/* ---------------------------------------------------------------------- the window's routes */
/**
 * The pages the desktop app's window may show, exactly (`docs/off-board-plan.md` §2.2,
 * `docs/library-first-plan.md` §5).
 *
 * The app is the library and nothing else. Its native side holds every *navigation* to this list
 * (`is_storefront_route` in the app's `site.rs`) and sends anything else to the system browser —
 * but a route change the page makes itself is not a navigation and never reaches it. So the page
 * holds its own to the same list (`holdNavigation` in the dashboard's router), and **this is that
 * list**: the Rust and this must say the same thing, and `apps/web/test/window-routes.test.ts`
 * reads the Rust to see that they do. A second copy of it anywhere is the bug.
 */
export const STOREFRONT_ROUTES = ['/library', '/get', '/redeem', '/link', '/signin', '/signup', '/forgot'];
/** A redeem code is the tester's to type; one segment of unreserved characters is all this knows. */
const REDEEM_CODE_RE = /^[A-Za-z0-9_-]{1,64}$/;
/** Whether a path — a pathname, with no query or fragment — is a page the app's window may show. */
export function isStorefrontRoute(path) {
    if (STOREFRONT_ROUTES.includes(path) || path.startsWith('/library/'))
        return true;
    if (path.startsWith('/g/'))
        return SHARE_CODE_RE.test(path.slice('/g/'.length));
    if (path.startsWith('/redeem/'))
        return REDEEM_CODE_RE.test(path.slice('/redeem/'.length));
    // A player's profile (`docs/achievements-plan.md` §12a.8): your own, or a username exactly as the
    // API stores one, lower case — the page redirects any other spelling to that one.
    if (path.startsWith('/u/')) {
        const name = path.slice('/u/'.length);
        return name === 'me' || isValidUsername(name);
    }
    return false;
}
/* ---------------------------------------------------------------------- deep links, read */
/** The whole link, matched in one piece: a known host, then at most one segment. */
const DEEP_LINK_RE = /^boardflight:\/\/(job|game|pair|open)(?:\/([^/?#]*))?$/;
/**
 * Read a deep link, or null for anything that is not exactly one of the four forms.
 *
 * Strict on purpose, and matched against the raw string rather than through `URL`, which would
 * normalise the things this exists to refuse. An id is a ULID or a share code and nothing looser,
 * so anything carrying a URL, a path, a query, a fragment, an encoded character, credentials, a
 * port, a trailing slash or an unknown host is not a deep link.
 */
export function parseDeepLink(input) {
    if (typeof input !== 'string' || input.length > 64)
        return null;
    const match = DEEP_LINK_RE.exec(input);
    if (!match)
        return null;
    const host = match[1];
    const id = match[2];
    if (host === 'pair' || host === 'open')
        return id === undefined ? { kind: host } : null;
    if (id === undefined)
        return null;
    if (host === 'job')
        return ULID_RE.test(id) ? { kind: 'job', jobId: id } : null;
    if (ULID_RE.test(id))
        return { kind: 'game', appId: id };
    if (SHARE_CODE_RE.test(id))
        return { kind: 'game', code: id };
    return null;
}
/**
 * Write a deep link. Throws on an id {@link parseDeepLink} would refuse, so a link that cannot be
 * read back is never handed to the operating system.
 */
export function formatDeepLink(link) {
    const url = link.kind === 'job'
        ? `${DEEP_LINK_SCHEME}://job/${link.jobId}`
        : link.kind === 'game'
            ? `${DEEP_LINK_SCHEME}://game/${'code' in link ? link.code : link.appId}`
            : `${DEEP_LINK_SCHEME}://${link.kind}`;
    const parsed = parseDeepLink(url);
    // Read back, and the kind of id checked as well: a share code handed in as an `appId` would
    // otherwise format to a link that reads back as something else.
    const same = parsed !== null &&
        parsed.kind === link.kind &&
        (link.kind !== 'game' || (parsed.kind === 'game' && 'code' in parsed === 'code' in link));
    if (!same)
        throw new RangeError('Not a valid BoardFlight deep link.');
    return url;
}
//# sourceMappingURL=board-link.js.map